import React from 'react';
import { BookOpen, ExternalLink } from 'lucide-react';
import { publications } from '../mock';
import { useTheme } from '../context/ThemeContext';

const Publications = () => {
  const { theme } = useTheme();

  const getTypeColor = (type) => {
    switch (type) {
      case 'Technical Blog':
        return 'bg-blue-500/10 border-blue-500/30 text-blue-500';
      case 'Conference Talk':
        return 'bg-purple-500/10 border-purple-500/30 text-purple-500';
      case 'Whitepaper':
        return 'bg-green-500/10 border-green-500/30 text-green-500';
      case 'Case Study':
        return 'bg-yellow-500/10 border-yellow-500/30 text-yellow-600';
      default:
        return theme === 'dark' 
          ? 'bg-white/10 border-white/30 text-white' 
          : 'bg-gray-100 border-gray-300 text-gray-700';
    }
  };

  return (
    <section id="publications" className={`py-12 sm:py-20 px-4 sm:px-6 lg:px-8 ${
      theme === 'dark' 
        ? 'bg-gradient-to-br from-[#0f1f26] to-[#1B3139]' 
        : 'bg-gradient-to-br from-white to-gray-100'
    }`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <div className="inline-flex items-center space-x-2 mb-4">
            <BookOpen className="h-5 w-5 sm:h-6 sm:w-6 text-[#FF3621]" />
            <span 
              className="text-[#FF3621] font-semibold text-xs sm:text-sm uppercase tracking-wider"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Knowledge Sharing
            </span>
          </div>
          <h2 
            className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Publications & Talks
          </h2>
          <p 
            className={`text-base sm:text-lg max-w-2xl mx-auto px-4 ${
              theme === 'dark' ? 'text-white/60' : 'text-gray-600'
            }`}
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Sharing insights and learnings with the data and AI community
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {publications.map((pub) => (
            <a 
              key={pub.id}
              href={pub.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`border rounded-lg p-4 sm:p-6 transition-all duration-300 shadow-xl hover:shadow-2xl group block ${
                theme === 'dark' 
                  ? 'bg-[#1B3139] border-white/10 hover:border-[#FF3621]/50' 
                  : 'bg-white border-gray-200 hover:border-[#FF3621]/50'
              }`}
            >
              <div className="flex items-start justify-between mb-3 sm:mb-4">
                <span 
                  className={`px-2 sm:px-3 py-1 border rounded-full text-xs font-medium ${getTypeColor(pub.type)}`}
                  style={{ fontFamily: 'DM Mono, monospace' }}
                >
                  {pub.type}
                </span>
                <span 
                  className={`text-xs font-mono ${
                    theme === 'dark' ? 'text-white/40' : 'text-gray-500'
                  }`}
                  style={{ fontFamily: 'DM Mono, monospace' }}
                >
                  {pub.year}
                </span>
              </div>

              <h3 
                className={`text-base sm:text-xl font-bold mb-3 group-hover:text-[#FF3621] transition-colors leading-tight ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {pub.title}
              </h3>

              <div className={`flex items-center justify-between pt-3 sm:pt-4 border-t ${
                theme === 'dark' ? 'border-white/10' : 'border-gray-200'
              }`}>
                <p 
                  className={`text-sm sm:text-base font-medium ${
                    theme === 'dark' ? 'text-white/60' : 'text-gray-500'
                  }`}
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  {pub.platform}
                </p>
                <ExternalLink className={`h-4 w-4 group-hover:text-[#FF3621] transition-colors flex-shrink-0 ${
                  theme === 'dark' ? 'text-white/40' : 'text-gray-400'
                }`} />
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Publications;
